/**
 * Move/resize math for the stage: resize handles that keep the opposite edge fixed, and
 * snapping to the canvas, other slots and a grid. All in canvas (1920×1080) pixels.
 */
import { REFERENCE_CANVAS, type Rect } from '@ioda/shared';

export const GRID = 10;
export const SNAP_THRESHOLD = 6;
export const ASPECT_16_9 = 16 / 9;
export const MIN_BOX = 16;

export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const HANDLES: readonly Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** A guide line to draw while snapped: vertical (`x`) or horizontal (`y`). */
export interface Guide {
  axis: 'x' | 'y';
  at: number;
}

/**
 * The rect after dragging `handle` by (dx, dy). The opposite edge/corner stays fixed.
 * With `lockAspect`, width/height keep `aspect`, driven by the dragged axis.
 */
export function resizeRect(start: Rect, handle: Handle, dx: number, dy: number, lockAspect = false, aspect = ASPECT_16_9): Rect {
  let { x, y, width, height } = start;
  const right = x + width;
  const bottom = y + height;
  if (handle.includes('e')) width = Math.max(MIN_BOX, start.width + dx);
  if (handle.includes('w')) width = Math.max(MIN_BOX, start.width - dx);
  if (handle.includes('s')) height = Math.max(MIN_BOX, start.height + dy);
  if (handle.includes('n')) height = Math.max(MIN_BOX, start.height - dy);
  if (lockAspect) {
    if (handle === 'n' || handle === 's') width = height * aspect;
    else height = width / aspect;
  }
  x = handle.includes('w') ? right - width : handle === 'n' || handle === 's' ? start.x + (start.width - width) / 2 : start.x;
  y = handle.includes('n') ? bottom - height : handle === 'e' || handle === 'w' ? start.y + (start.height - height) / 2 : start.y;
  return { x, y, width, height };
}

interface Lines {
  x: number[];
  y: number[];
}

/** Snap targets: canvas edges and centre, plus every other rect's edges and centre. */
export function snapTargets(others: readonly Rect[]): Lines {
  const x = [0, REFERENCE_CANVAS.width / 2, REFERENCE_CANVAS.width];
  const y = [0, REFERENCE_CANVAS.height / 2, REFERENCE_CANVAS.height];
  for (const r of others) {
    x.push(r.x, r.x + r.width / 2, r.x + r.width);
    y.push(r.y, r.y + r.height / 2, r.y + r.height);
  }
  return { x, y };
}

function best(candidates: readonly number[], targets: readonly number[], threshold: number): { delta: number; at: number } | null {
  let found: { delta: number; at: number } | null = null;
  for (const c of candidates) {
    for (const t of targets) {
      const delta = t - c;
      if (Math.abs(delta) <= threshold && (found === null || Math.abs(delta) < Math.abs(found.delta))) found = { delta, at: t };
    }
  }
  return found;
}

const toGrid = (v: number, grid: number) => (grid > 0 ? Math.round(v / grid) * grid : v);

/** Snaps a moved rect: guides first (edges and centre), else its top-left to the grid. */
export function snapMove(rect: Rect, others: readonly Rect[], grid = GRID, threshold = SNAP_THRESHOLD): { rect: Rect; guides: Guide[] } {
  const targets = snapTargets(others);
  const guides: Guide[] = [];
  let { x, y } = rect;
  const sx = best([rect.x, rect.x + rect.width / 2, rect.x + rect.width], targets.x, threshold);
  if (sx) {
    x += sx.delta;
    guides.push({ axis: 'x', at: sx.at });
  } else x = toGrid(x, grid);
  const sy = best([rect.y, rect.y + rect.height / 2, rect.y + rect.height], targets.y, threshold);
  if (sy) {
    y += sy.delta;
    guides.push({ axis: 'y', at: sy.at });
  } else y = toGrid(y, grid);
  return { rect: { ...rect, x, y }, guides };
}

/**
 * Snaps the edges a resize handle moves. Skipped with `lockAspect`, where snapping one
 * edge would break the ratio.
 */
export function snapResize(rect: Rect, handle: Handle, others: readonly Rect[], lockAspect: boolean, grid = GRID, threshold = SNAP_THRESHOLD): { rect: Rect; guides: Guide[] } {
  if (lockAspect) return { rect, guides: [] };
  const targets = snapTargets(others);
  const guides: Guide[] = [];
  let { x, y, width, height } = rect;
  const snapEdge = (value: number, axis: 'x' | 'y'): number => {
    const s = best([value], axis === 'x' ? targets.x : targets.y, threshold);
    if (s) {
      guides.push({ axis, at: s.at });
      return s.at;
    }
    return toGrid(value, grid);
  };
  if (handle.includes('e')) width = Math.max(MIN_BOX, snapEdge(x + width, 'x') - x);
  if (handle.includes('w')) {
    const right = x + width;
    x = snapEdge(x, 'x');
    width = Math.max(MIN_BOX, right - x);
  }
  if (handle.includes('s')) height = Math.max(MIN_BOX, snapEdge(y + height, 'y') - y);
  if (handle.includes('n')) {
    const bottom = y + height;
    y = snapEdge(y, 'y');
    height = Math.max(MIN_BOX, bottom - y);
  }
  return { rect: { x, y, width, height }, guides };
}
